import { api, ApiError } from './client'
import type { UsageRowLike } from '../components/UsageConfirmDialog'

export interface Pipeline {
  id: number
  name: string
  departmentId: number | null
  isDefault: boolean
  sortOrder: number
}

/**
 * TASK-0041 fix: this used to declare `isClosedWon`/`isClosedLost` booleans that
 * `EqualCrm.Modules.Pipelines.Domain.PipelineStage` (the backend entity `PipelinesController.Get`
 * serializes verbatim) has never actually had — that entity's real fields are `isFinal: boolean` +
 * `finalStatus: string | null` (`"won"|"lost"|"converted"`). The old field names meant
 * `stage.isClosedWon`/`stage.isClosedLost` were always `undefined` or unpredictable at runtime, so
 * PipelinesPage.tsx's won/lost stage icon never actually reflected real data — found while wiring
 * this task's Kanban board, fixed here since it is the type this task's own move-endpoint
 * documentation (the non-coupling constraint) talks about. Out of allowed_paths is
 * Pipelines/Domain/PipelineStage.cs itself, which is untouched — this only corrects the frontend's
 * mirror of its actual shape.
 */
export interface PipelineStage {
  id: number
  name: string
  color: string
  sortOrder: number
  /** F-129: fase iniziale della pipeline (una sola). */
  isDefault: boolean
  isFinal: boolean
  finalStatus: 'won' | 'lost' | 'converted' | null
}

export interface PipelineDetail extends Pipeline {
  stages: PipelineStage[]
  description?: string | null
  isActive?: boolean
}

/** F-142 (REQ-293-001): riga di "dove e' usato" di una pipeline o di una gara (stessa forma del catalogo, con `code`). */
export interface ConfigurationUsageRow extends UsageRowLike {
  code: string
}

export function fetchConfigurationUsages(kind: 'pipeline' | 'contest', id: number): Promise<ConfigurationUsageRow[]> {
  return api<ConfigurationUsageRow[]>(`/api/configuration-usages/${kind}/${id}`)
}

export type GuardedResult = { status: 'done' } | { status: 'needs-confirm'; usages: ConfigurationUsageRow[] }

/**
 * F-142 (REQ-293-001): esegue una richiesta che con la guardia accesa puo' rispondere 409 `{ usages }`.
 * Il 409 diventa `needs-confirm` (si chiede conferma e si ripete con `confirm=true`); ogni altro errore resta un errore.
 */
export async function guardedRequest(run: () => Promise<unknown>): Promise<GuardedResult> {
  try {
    await run()
    return { status: 'done' }
  } catch (e) {
    const body = e instanceof ApiError ? (e.body as { usages?: ConfigurationUsageRow[] } | null) : null
    if (body && Array.isArray(body.usages)) return { status: 'needs-confirm', usages: body.usages }
    throw e
  }
}

/** Disattiva la pipeline (PUT con isActive=false). Con usi bloccanti e guardia accesa risponde 409: si chiede conferma. */
export function deactivatePipeline(p: PipelineDetail, confirm: boolean): Promise<GuardedResult> {
  return guardedRequest(() => api<void>(`/api/pipelines/${p.id}${confirm ? '?confirm=true' : ''}`, {
    method: 'PUT',
    body: JSON.stringify({ name: p.name, description: p.description ?? null, departmentId: p.departmentId, isActive: false }),
  }))
}

/** F-129: sceglie la fase iniziale della pipeline (le altre perdono il flag). 400 se la fase è finale. */
export function setInitialStage(pipelineId: number, stageId: number): Promise<void> {
  return api<void>(`/api/pipelines/${pipelineId}/stages/${stageId}/default`, { method: 'PUT' })
}

export function fetchPipelines(): Promise<Pipeline[]> {
  return api<Pipeline[]>('/api/pipelines')
}

export function fetchPipeline(id: number): Promise<PipelineDetail> {
  return api<PipelineDetail>(`/api/pipelines/${id}`)
}

// -----------------------------------------------------------------------
// TASK-0041 (REQ-005-001/002): Kanban board data + move action.
//
// LeadsController.List/ContractsController.List gained pipelineId/stageId query filters instead
// of a dedicated Kanban-listing endpoint. This client fetches every card of the selected pipeline
// in a SINGLE request per entity type (pipelineId filter only, a generously large pageSize) and
// groups the result by stageId client-side to build the columns — rather than one request per
// column — because a pipeline's board is dominated by request round-trips, not payload size (a
// pipeline realistically holds a few hundred leads/contracts at most), and a single call keeps the
// column counts/sums trivially consistent with each other (no risk of columns being fetched at
// slightly different times and disagreeing about a card that moved in between).
//
// Lives here (not api/leads.ts / api/contracts.ts) because those files are outside this task's
// allowed_paths — the Kanban's own client for its own extended filters/move endpoint belongs with
// the page that owns it, api/pipelines.ts, per the task card's point 4.

export interface LeadKanbanCard {
  id: number
  firstName: string | null
  lastName: string | null
  companyName: string | null
  email: string | null
  status: string
  priority: string
  leadType: string
  assignedAgentId: number | null
  pipelineId: number | null
  stageId: number | null
  /**
   * TASK-0067: added to `LeadsController.List`'s projection for the Kanban "Filtri" panel's
   * Reparto select. Real field on the `Lead` entity, but verified (see this task's report) to be
   * NEVER written by any path in this codebase today — the app has no create/update flow that
   * sets it, and the legacy-data migration (`SeedLeads.cs`) does not import it either. So it is
   * always `null` on every lead that exists right now; filtering by it is still genuinely wired
   * (mechanically correct, will start working the moment something populates it), just not
   * useful against today's data. Out of this task's scope to also add a way to set it.
   */
  departmentId: number | null
  estimatedValue: number | null
  createdAt: string
}

export interface ContractKanbanCard {
  id: number
  referenceCode: string
  status: string
  customerId: number | null
  /** Nome del cliente (azienda o contatto della pratica), dall'elenco pratiche. */
  customerName?: string | null
  agentId: number | null
  productId: number | null
  pipelineId: number | null
  stageId: number | null
  /** TASK-0067: added to `ContractsController.List`'s projection for the Kanban "Filtri" panel's
   * Reparto select. Unlike `LeadKanbanCard.departmentId` below, this one IS populated on real data
   * — the legacy migration (`SeedContracts.cs`) imports `department_id` from the source system. */
  departmentId: number | null
  /**
   * TASK-0067: added to `ContractsController.List`'s projection for the Kanban "Filtri" panel's
   * Operatore select (contract-only — Lead has no analogous field, verified by reading
   * `Lead.cs`). Real field/column on `Contract`, but verified (see this task's report) to be
   * NEVER written by any path in this codebase — no domain method sets it and the legacy-data
   * migration doesn't import it either (the "operator" concept the rest of the app actually
   * writes/reads is `Product.OperatorId` in the Catalog module, a different field entirely, e.g.
   * `BatchesController`'s `IProductOperatorReader`). So this is always `null` today; the filter is
   * genuinely wired against a real column, just not useful against today's data.
   */
  operatorId: number | null
  baseAmount: number
  expectedAmount: number | null
  paymentStatus: string
  createdAt: string
}

interface KanbanListResponse<T> {
  total: number
  page: number
  pageSize: number
  items: T[]
}

/** How many cards each request asks for. NOT the cap on how many the board ends up with — see
 * `fetchAllPages`, which keeps requesting until the server's own `total` is satisfied. */
const KANBAN_PAGE_SIZE = 500

/**
 * Hard ceiling on cards pulled for one board, as a runaway guard only. Deliberately far above any
 * realistic pipeline so it never truncates in practice, unlike the single-page fetch this replaced.
 */
const KANBAN_MAX_CARDS = 10000

/**
 * TASK-0067 independent review — bug fix.
 *
 * These two functions used to issue exactly ONE request of `pageSize=500` and return
 * `res.items`, on the stated assumption (see the remarks at the top of this section) that "a
 * pipeline realistically holds a few hundred leads/contracts at most". That assumption is false
 * against real data: the production pipeline #1 holds 611 contracts, so the board silently
 * dropped 111 of them — it rendered "500 card" for a 611-card pipeline.
 *
 * Harmless-ish while the board was only ever a display, that truncation became a correctness bug
 * the moment TASK-0067 made the "Filtri" panel filter CLIENT-SIDE over this loaded set: filtering
 * by Reparto = Energia showed 310 cards when the pipeline genuinely contains 421, with nothing in
 * the UI hinting the answer was computed over a truncated sample. A filter that quietly returns
 * the wrong count is worse than the dead filter panel the task set out to fix.
 *
 * The response already carries `total`, so the fix is to keep paging until we have it. Almost
 * always still a single round-trip (any pipeline under 500 cards is unchanged); 611 contracts now
 * costs two. `KANBAN_MAX_CARDS` bounds the pathological case, and the loop also stops on any short
 * or empty page so a server-side `pageSize` clamp can never turn this into an infinite loop.
 */
async function fetchAllPages<T>(buildUrl: (page: number) => string): Promise<T[]> {
  const all: T[] = []
  for (let page = 1; ; page++) {
    const res = await api<KanbanListResponse<T>>(buildUrl(page))
    all.push(...res.items)
    if (
      res.items.length === 0 ||
      res.items.length < KANBAN_PAGE_SIZE ||
      all.length >= res.total ||
      all.length >= KANBAN_MAX_CARDS
    ) {
      return all
    }
  }
}

export function fetchLeadsForPipeline(pipelineId: number): Promise<LeadKanbanCard[]> {
  return fetchAllPages<LeadKanbanCard>(
    page => `/api/leads?pipelineId=${pipelineId}&page=${page}&pageSize=${KANBAN_PAGE_SIZE}`,
  )
}

export function fetchContractsForPipeline(pipelineId: number): Promise<ContractKanbanCard[]> {
  return fetchAllPages<ContractKanbanCard>(
    page => `/api/contracts?pipelineId=${pipelineId}&page=${page}&pageSize=${KANBAN_PAGE_SIZE}`,
  )
}

/**
 * Moves a lead into a pipeline stage — calls the new `PATCH /api/leads/{id}/pipeline`, which
 * invokes the domain's own `Lead.AssignPipeline` (previously dead code, zero callers). Deliberately
 * does not, and must never, touch `Lead.Status` — see PipelinesPage.tsx's remarks on
 * `handleMoveCard` for the non-coupling constraint this task is bound by.
 */
export function moveLeadToStage(id: number, pipelineId: number, stageId: number): Promise<void> {
  return api<void>(`/api/leads/${id}/pipeline`, {
    method: 'PATCH',
    body: JSON.stringify({ pipelineId, stageId }),
  })
}

/**
 * Moves a contract into a pipeline stage — calls the new `PATCH /api/contracts/{id}/pipeline`,
 * which invokes the domain's own `Contract.SetPipeline` (previously dead code, zero callers).
 * Deliberately does not, and must never, touch `Contract.Status` — see PipelinesPage.tsx's remarks
 * on `handleMoveCard` for the non-coupling constraint this task is bound by.
 */
export function moveContractToStage(id: number, pipelineId: number, stageId: number): Promise<void> {
  return api<void>(`/api/contracts/${id}/pipeline`, {
    method: 'PATCH',
    body: JSON.stringify({ pipelineId, stageId }),
  })
}
